import { Pencil, Trash2 } from "lucide-react";
import { Modal2, ApproveRejectActions, SimpleTooltip } from "../../../common";
import { useState } from "react";
import { getLabelForModules } from "../../../../utils/dynamicworkflow/workflowUtils";
import { formatDateDDMMMYYYY } from "../../../../utils/timeUtils";

export interface WorkflowModule {
  id: string;
  name: string;
  description: string;
  module: string;
  isActive: boolean;
  levels: { label: string; type: "employee" | "level" }[];
  createdAt: string;
}

interface WorkflowCardProps {
  workflow: WorkflowModule;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  isDeleting?: boolean;
  onClick?: () => void;
  isSelected?: boolean;
}

const statusStyles: Record<string, string> = {
  active: "bg-[var(--color-success-light)] text-[var(--color-success-dark)]",
  inactive: "bg-[var(--color-error-light)] text-[var(--color-error-dark)]",
};


export function WorkflowCard({
  workflow,
  onEdit,
  onDelete,
  isDeleting,
  onClick,
  isSelected,
}: WorkflowCardProps) {
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  return (
    <>
      <div
        onClick={onClick}
        className={`bg-[var(--color-bg-base)] border rounded-[18px] p-5 flex flex-col gap-3 transition-all duration-150 hover:-translate-y-[3px] hover:shadow-[var(--shadow-md)] ${isSelected
          ? "border-[var(--color-primary-400)] ring-2 ring-[var(--color-primary-100)]"
          : "border-[var(--color-border-secondary)] shadow-[var(--shadow-sm)]"
          } ${onClick ? "cursor-pointer" : ""}`}
      >
        {/* Info */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="font-bold text-[var(--color-text-primary)] text-[15.5px] truncate mb-0 lowercase">
              {workflow.name}
            </h3>
            <span
              className={`px-2 py-0.5 text-[11.5px] font-bold rounded-full capitalize flex-none ${statusStyles[workflow.isActive ? "active" : "inactive"]}`}
            >
              {workflow.isActive ? "Active" : "Inactive"}
            </span>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(workflow.id); }}
              className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-muted)]"
            >
              <SimpleTooltip label="Edit" side="top">
                <Pencil className="w-4 h-4" />
              </SimpleTooltip>
            </button>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setDeleteConfirm(true); }}
              className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-error-light)] hover:text-[var(--color-error-dark)]"
            >
              <SimpleTooltip label="Delete" side="top">
                <Trash2 className="w-4 h-4" />
              </SimpleTooltip>
            </button>
          </div>
        </div>

        <p className="text-[13px] text-[var(--color-text-secondary)] mb-0 truncate">
          {workflow.description}
        </p>

        <p className="text-xs text-[var(--color-text-tertiary)] mb-0">
          Module:{" "}
          <span className="font-semibold text-[var(--color-text-secondary)] lowercase capitalize">
            {getLabelForModules(workflow.module)}
          </span>
          <span className="mx-2">·</span>
          Created:{" "}
          <span className="font-semibold text-[var(--color-text-secondary)]">
            {formatDateDDMMMYYYY(workflow.createdAt)}
          </span>
        </p>
      </div>

      <Modal2
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        title="Delete Workflow"
        size="sm"
        footer={
          <ApproveRejectActions
            onApprove={() => {
              onDelete(workflow.id);
              setDeleteConfirm(false);
            }}
            onReject={() => setDeleteConfirm(false)}
            approveLoading={isDeleting}
            disabled={isDeleting}
            approveLabel="Delete"
            rejectLabel="Cancel"
            approveIcon={<Trash2 className="w-4 h-4" />}
          />
        }
      >
        <div className="text-[13px] text-[var(--color-text-secondary)]">
          Are you sure you want to delete this workflow? This action cannot be undone.
        </div>
      </Modal2>
    </>
  );
}
